// Menümü Kişiselleştir — user-scoped drag-drop menu personalization.
// Uses POINTER EVENTS instead of HTML5 native drag/drop for a MUCH more
// reliable, low-latency drag experience:
//   • pointerdown  → immediate pointer capture (no threshold, no browser
//                    "drag image" flicker, works on touch + mouse)
//   • pointermove  → live drop-target detection via document.elementFromPoint
//                    with a floating ghost following the cursor
//   • pointerup    → applies the last valid dropHint
// Any page can be moved to any group, to KÖK MENÜ (top-level), or reordered
// freely. Route / permission / module id NEVER change — this is a personal
// visual overlay stored in menu_user_prefs.
import React from "react";
import { api } from "@/lib/api";
import { Modal, Btn } from "@/components/UI";
import {
  Eye, EyeOff, GripVertical, RotateCcw, Save, Info,
  ArrowUpToLine, ChevronDown, ChevronRight, Target,
} from "lucide-react";
import { TOP_LEVEL_GID } from "@/lib/navigation";

function buildInitial(assembled, prefs) {
  const cp = prefs?.custom_parent || {};
  const orderMap = prefs?.item_order || {};
  const validGids = new Set(assembled.map((g) => g.gid));
  const byGid = new Map(assembled.map((g) => [g.gid, { ...g, items: [] }]));
  for (const g of assembled) {
    for (const it of g.items) {
      let tg = cp[it.id];
      if (!tg || !validGids.has(tg)) tg = g.gid;
      byGid.get(tg).items.push({ ...it, parentGid: tg });
    }
  }
  for (const g of byGid.values()) {
    const ord = orderMap[g.gid];
    if (Array.isArray(ord) && ord.length) {
      const idx = new Map(ord.map((id, i) => [id, i]));
      g.items.sort((a, b) => {
        const ai = idx.has(a.id) ? idx.get(a.id) : 10000;
        const bi = idx.has(b.id) ? idx.get(b.id) : 10000;
        return ai - bi;
      });
    }
  }
  return assembled.map((g) => byGid.get(g.gid));
}

export default function MenuUserPrefsModal({ open, onClose, assembled, initialPrefs, onSaved }) {
  const [nav, setNav] = React.useState(() => buildInitial(assembled, initialPrefs));
  const [hidden, setHidden] = React.useState(() => new Set(initialPrefs?.hidden_ids || []));
  const [busy, setBusy] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(new Set());
  // Active drag session (pointer-events based).
  const [drag, setDrag] = React.useState(null);        // {itemId, fromGid, label, x, y}
  const [dropHint, setDropHint] = React.useState(null); // {gid} or {gid,itemId,pos}
  const dragRef = React.useRef(null);
  const hintRef = React.useRef(null);
  const scrollRef = React.useRef(null);

  // ─── Hide / Collapse ───────────────────────────────────────────
  const toggleHide = (id) => {
    setHidden((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleCollapse = (gid) => {
    setCollapsed((p) => { const n = new Set(p); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });
  };

  // ─── Pointer-based drag ────────────────────────────────────────
  const beginDrag = (id, gid, label) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;   // left / touch only
    // Prevent text selection during drag
    e.preventDefault();
    const session = { itemId: id, fromGid: gid, label, x: e.clientX, y: e.clientY };
    dragRef.current = session;
    setDrag(session);
    hintRef.current = null;
    setDropHint(null);
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
  };

  const updateHintFromPoint = React.useCallback((clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) { hintRef.current = null; setDropHint(null); return; }
    // Prefer item drop zone (finer target)
    const itemNode = el.closest("[data-drop-item]");
    if (itemNode) {
      const gid = itemNode.getAttribute("data-drop-gid");
      const itemId = itemNode.getAttribute("data-drop-item");
      const rect = itemNode.getBoundingClientRect();
      const pos = (clientY - rect.top) < rect.height / 2 ? "before" : "after";
      const h = { kind: "at-item", gid, itemId, pos };
      hintRef.current = h;
      setDropHint(h);
      return;
    }
    const groupNode = el.closest("[data-drop-group]");
    if (groupNode) {
      const gid = groupNode.getAttribute("data-drop-group");
      const h = { kind: "into-group", gid };
      hintRef.current = h;
      setDropHint(h);
      return;
    }
    hintRef.current = null;
    setDropHint(null);
  }, []);

  const onPointerMove = React.useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = { ...dragRef.current, x: e.clientX, y: e.clientY };
    setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    updateHintFromPoint(e.clientX, e.clientY);
    // Auto-scroll modal body when near edges
    const sc = scrollRef.current;
    if (sc) {
      const r = sc.getBoundingClientRect();
      const edge = 40;
      if (e.clientY - r.top < edge) sc.scrollTop -= 12;
      else if (r.bottom - e.clientY < edge) sc.scrollTop += 12;
    }
  }, [updateHintFromPoint]);

  const performDrop = (session, hint) => {
    if (!hint) return;
    const { itemId, fromGid } = session;
    if (hint.kind === "at-item") {
      if (hint.itemId === itemId) return;
      setNav((prev) => {
        const arr = prev.map((g) => ({ ...g, items: [...g.items] }));
        const src = arr.find((g) => g.gid === fromGid);
        const dst = arr.find((g) => g.gid === hint.gid);
        if (!src || !dst) return prev;
        const i = src.items.findIndex((it) => it.id === itemId);
        if (i < 0) return prev;
        const [moved] = src.items.splice(i, 1);
        moved.parentGid = hint.gid;
        let t = dst.items.findIndex((it) => it.id === hint.itemId);
        if (t < 0) t = dst.items.length;
        dst.items.splice(hint.pos === "before" ? t : t + 1, 0, moved);
        return arr;
      });
    } else if (hint.kind === "into-group") {
      setNav((prev) => {
        const arr = prev.map((g) => ({ ...g, items: [...g.items] }));
        const src = arr.find((g) => g.gid === fromGid);
        const dst = arr.find((g) => g.gid === hint.gid);
        if (!src || !dst) return prev;
        const i = src.items.findIndex((it) => it.id === itemId);
        if (i < 0) return prev;
        const [moved] = src.items.splice(i, 1);
        moved.parentGid = hint.gid;
        dst.items.push(moved);
        return arr;
      });
    }
  };

  const endDrag = React.useCallback(() => {
    if (dragRef.current) {
      performDrop(dragRef.current, hintRef.current);
    }
    dragRef.current = null;
    hintRef.current = null;
    setDrag(null);
    setDropHint(null);
  }, []);

  // Global fallback listeners while drag is active — in case pointer
  // leaves the row element (e.g. moving over a scrollbar).
  React.useEffect(() => {
    if (!drag) return;
    const move = (e) => onPointerMove(e);
    const up = () => endDrag();
    const cancel = () => endDrag();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [drag, onPointerMove, endDrag]);

  // ─── Save / Reset ──────────────────────────────────────────────
  const toPayload = () => {
    const globalGid = new Map();
    for (const g of assembled) for (const it of g.items) globalGid.set(it.id, g.gid);
    const custom_parent = {};
    const item_order = {};
    for (const g of nav) {
      item_order[g.gid] = g.items.map((it) => it.id);
      for (const it of g.items) {
        if (globalGid.get(it.id) !== g.gid) custom_parent[it.id] = g.gid;
      }
    }
    return { hidden_ids: Array.from(hidden), item_order, custom_parent };
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/menu/user-prefs", toPayload());
      onSaved?.({
        hidden_ids: r.hidden_ids || [],
        item_order: r.item_order || {},
        custom_parent: r.custom_parent || {},
      });
      onClose?.();
    } catch (e) {
      alert("Kaydedilemedi: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Kişisel menü tercihiniz sıfırlanacak. Devam edilsin mi?")) return;
    setBusy(true);
    try {
      await api.delete("/menu/user-prefs");
      onSaved?.({ hidden_ids: [], item_order: {}, custom_parent: {} });
      onClose?.();
    } catch (e) {
      alert("Sıfırlama başarısız: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const isDragging = !!drag;

  return (
    <Modal open={open} onClose={onClose} title="Menümü Kişiselleştir" size="lg">
      <div className="flex items-start gap-2 p-2.5 border border-indigo-200 bg-indigo-50/50 rounded text-xs mb-3">
        <Info size={14} className="text-indigo-600 shrink-0 mt-0.5"/>
        <div className="text-slate-700">
          <b>Hızlı sürükle-bırak:</b> herhangi bir sayfa satırına <b>basılı tut</b> ve istediğiniz gruba, sayfanın üstüne/altına ya da <b>KÖK MENÜ</b>'ye taşıyın. Yalnız <b>sizin görünümünüzü</b> etkiler; rota ve yetkiler değişmez.
        </div>
      </div>

      {drag && (
        <div className="flex items-center gap-2 p-2 mb-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900" data-testid="user-prefs-drag-status">
          <Target size={14}/>
          <span>Sürükleniyor: <b>{drag.label}</b> — hedef alana bırakın</span>
        </div>
      )}

      <div ref={scrollRef} className="space-y-2 max-h-[60vh] overflow-y-auto pr-1 select-none" data-testid="user-prefs-body">
        {nav.map((g) => {
          const isTop = g.gid === TOP_LEVEL_GID;
          const isCollapsed = collapsed.has(g.gid);
          const highlighted = dropHint?.kind === "into-group" && dropHint.gid === g.gid;
          const cls = isTop
            ? "border-dashed border-amber-400 bg-amber-50/40"
            : "border-slate-200 bg-slate-50/40";
          const dropCls = isDragging ? " border-2 border-indigo-400 bg-indigo-50/30" : "";
          const ringCls = highlighted ? " ring-4 ring-indigo-400/60 shadow-lg" : "";
          const label = isTop ? "KÖK MENÜ (Grupsuz Üst Seviye)" : g.displayLabel;
          return (
            <div
              key={g.gid}
              data-drop-group={g.gid}
              className={`border rounded transition-all ${cls}${dropCls}${ringCls}`}
              data-testid={`user-prefs-group-${g.gid}`}
            >
              <div className="flex items-center gap-2 px-2 py-1.5">
                {isTop
                  ? <ArrowUpToLine size={13} className="text-amber-600 shrink-0"/>
                  : <span className="w-3.5 shrink-0"/>}
                <button type="button" onClick={() => toggleCollapse(g.gid)} className="p-0.5 text-slate-500 hover:text-slate-800">
                  {isCollapsed ? <ChevronRight size={13}/> : <ChevronDown size={13}/>}
                </button>
                <span className={`flex-1 text-[11px] font-semibold uppercase tracking-wider truncate ${
                  isTop ? "text-amber-800" : "text-slate-600"
                }`}>{label}</span>
                {isDragging && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 border border-indigo-300 text-indigo-800 font-semibold uppercase animate-pulse">
                    buraya bırak
                  </span>
                )}
                <span className="text-[10px] text-slate-400">{g.items.length} sayfa</span>
              </div>
              {(!isCollapsed || isDragging) && (
                <div className="p-1.5 space-y-1">
                  {g.items.length === 0 && (
                    <div className={`text-xs italic px-2 py-3 rounded border-2 border-dashed ${
                      isDragging ? "border-indigo-400 bg-indigo-50 text-indigo-700 font-semibold" : "border-transparent text-slate-400"
                    }`}>
                      {isTop
                        ? "Buraya bir sayfa sürükleyerek üst seviye (grupsuz) yapabilirsiniz."
                        : "Bu gruba sayfa sürükleyebilirsiniz."}
                    </div>
                  )}
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const isHidden = hidden.has(it.id);
                    const beingDragged = drag?.itemId === it.id;
                    const hint = dropHint?.kind === "at-item" && dropHint.itemId === it.id ? dropHint.pos : null;
                    return (
                      <div key={it.id} className="relative">
                        {hint === "before" && <div className="h-1 bg-indigo-500 rounded my-0.5 shadow" data-testid={`user-prefs-drop-before-${it.id}`}/>}
                        <div
                          data-drop-item={it.id}
                          data-drop-gid={g.gid}
                          data-testid={`user-prefs-item-${it.id}`}
                          onPointerDown={beginDrag(it.id, g.gid, it.displayLabel)}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-grab active:cursor-grabbing transition-all touch-none ${
                            beingDragged
                              ? "border-indigo-400 opacity-40"
                              : isHidden
                                ? "bg-slate-100 border-slate-200 opacity-60"
                                : "bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm"
                          }`}
                          style={{ touchAction: "none" }}
                        >
                          <GripVertical size={12} className="text-slate-400 shrink-0"/>
                          <Icon size={13} className={`text-${it.accent || "indigo"}-500 shrink-0`}/>
                          <span className={`flex-1 text-sm truncate ${isHidden ? "line-through text-slate-500" : "text-slate-700"}`}>{it.displayLabel}</span>
                          <code className="text-[10px] text-slate-400 hidden md:inline">{it.to}</code>
                          <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); toggleHide(it.id); }}
                            data-testid={`user-prefs-hide-${it.id}`}
                            className={`p-0.5 rounded ${isHidden ? "text-slate-400 hover:bg-slate-200" : "text-emerald-600 hover:bg-emerald-50"}`}
                            title={isHidden ? "Göster" : "Gizle"}>
                            {isHidden ? <EyeOff size={13}/> : <Eye size={13}/>}
                          </button>
                        </div>
                        {hint === "after" && <div className="h-1 bg-indigo-500 rounded my-0.5 shadow" data-testid={`user-prefs-drop-after-${it.id}`}/>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Floating "ghost" that follows the cursor while dragging */}
      {drag && (
        <div
          className="fixed z-[9999] pointer-events-none px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold shadow-2xl border border-indigo-400"
          style={{ left: drag.x + 14, top: drag.y + 14 }}
          data-testid="user-prefs-drag-ghost"
        >
          {drag.label}
        </div>
      )}

      <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-slate-200">
        <Btn variant="secondary" onClick={reset} disabled={busy} data-testid="user-prefs-reset">
          <RotateCcw size={13}/> Varsayılana Dön
        </Btn>
        <Btn variant="secondary" onClick={onClose} disabled={busy} data-testid="user-prefs-cancel">İptal</Btn>
        <Btn variant="primary" onClick={save} disabled={busy} data-testid="user-prefs-save">
          <Save size={13}/> Kaydet
        </Btn>
      </div>
    </Modal>
  );
}
